import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { CoverageGapRequester } from "@shared/types/corpus/api.ts";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import { Section } from "../../../../components/primitives/layout/Section";
import { gaps as spacing } from "../../../../components/primitives/spacing";
import { useAsyncPaginatedData } from "../../../../hooks/useAsyncPaginatedData";
import { getCoverageGapRequesters, markCoverageGapRequesterTold } from "../../../../lib/corpusApi";
import { formatDate } from "../../../../lib/date";
import { PAGE_SIZE_OPTIONS } from "../../../../lib/pagination";

export interface CoverageGapRequestersProps {
  gapId: string;
  /** Called after a person is marked told or not, as the gap's count of people to tell moves. */
  onChanged: () => Promise<void>;
}

/** The people to tell about a closed coverage gap: who asked, about which claim, and what became of
 *  it, with the means to record that they were told. The product sends nothing; the operator
 *  writes to them herself. Give it a `key` that changes with the gap. */
export function CoverageGapRequesters({ gapId, onChanged }: CoverageGapRequestersProps) {
  const requesters = useAsyncPaginatedData((page) => getCoverageGapRequesters(gapId, page), {
    pageSize: 25,
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const { refresh } = requesters;

  async function setTold(requester: CoverageGapRequester, told: boolean) {
    setActionError(null);
    try {
      await markCoverageGapRequesterTold(gapId, requester.id, told);
      await Promise.all([refresh(), onChanged()]);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Something went wrong");
    }
  }

  const columns: DataSequenceColumn<CoverageGapRequester>[] = [
    { key: "email", header: "Email", width: 240, cardRole: "primary", cell: (r) => r.email },
    { key: "claim", header: "Asked about", width: 360, cell: (r) => r.claim.canonical },
    {
      key: "outcome",
      header: "Outcome",
      cell: (r) =>
        r.outcome === "answered"
          ? `Answered${r.verdict ? `: ${r.verdict}` : ""}`
          : "No research exists",
    },
    { key: "asked", header: "Asked", cell: (r) => formatDate(r.askedAt) },
    {
      key: "told",
      header: "",
      cardRole: "action",
      cell: (r) =>
        r.toldAt === null ? (
          <Button size="small" variant="outlined" onClick={() => void setTold(r, true)}>
            Mark told
          </Button>
        ) : (
          <Stack direction="row" gap={spacing.snug} sx={{ alignItems: "center" }}>
            <Chip size="small" label={`Told ${formatDate(r.toldAt)}`} />
            <Button size="small" onClick={() => void setTold(r, false)}>
              Undo
            </Button>
          </Stack>
        ),
    },
  ];

  return (
    <Section>
      <Typography variant="body2" color="text.secondary">
        Everyone who asked to be told about a claim of this gap that now has an outcome. Write to
        them yourself, then mark them told.
      </Typography>
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {requesters.loading ? (
        <LoadingState />
      ) : requesters.error ? (
        <Alert severity="error">{requesters.error}</Alert>
      ) : (
        <DataSequence
          columns={columns}
          rows={requesters.items}
          getRowId={(r) => r.id}
          pagination={{ ...requesters.pagination, pageSizeOptions: PAGE_SIZE_OPTIONS }}
          emptyMessage="No one asked to be told about this gap's claims, or none has an outcome yet."
        />
      )}
    </Section>
  );
}
